import { NavigationService } from 'services/schemableFormNavigation/schemableFormNavigation';

import {
  isRowPath,
  ProductValidationItem,
  rowIndexOf,
  rowPath,
  withoutEditedField,
  withoutRowErrors,
} from './productValidationErrors';

const FOCUSABLE = 'input, button, select, textarea, [tabindex]';

/**
 * Carries the server's 422 errors for a save to the table's cells and rows, and to the banner that
 * cycles through them. `DataTable`'s cells read their error state from a `NavigationService`
 * (`useFieldStateFromAnnotations`) and require one in their ancestry; this page's only items are
 * the errors of the save that just failed.
 *
 * Its items are exactly the stops of the previous/next controls: one per cell error, one per error
 * about a row as a whole. A row that has any of them is highlighted (`useItemByFieldPath`), but the
 * highlight is not a stop of its own.
 *
 * One instance per page, never a shared singleton: the store is global, mutable state, and errors
 * from one visit must not be waiting on the next.
 */
export class ProductValidationService extends NavigationService<ProductValidationItem> {
  private readonly highlights = new Map<string, ProductValidationItem>();
  private readonly itemByFieldPath = this.useItemByFieldPath;

  /**
   * `DataTable` asks for the item at a row's own path to decide whether to highlight the row. A row
   * with an error about the row itself has that item; a row with only cell errors gets a message-less
   * stand-in — one object per row, for good, since a selector that returned a fresh object each
   * time would re-render every row on every store update.
   */
  useItemByFieldPath = (fieldPath: string): ProductValidationItem | undefined => {
    const item = this.itemByFieldPath(fieldPath);
    const highlight = this.store((state) =>
      isRowPath(fieldPath) && state.items.some((candidate) => rowIndexOf(candidate.fieldPath) === rowIndexOf(fieldPath))
        ? this.highlightFor(fieldPath)
        : undefined,
    );
    return item ?? highlight;
  };

  /** Puts the cursor's error in front of the user: focuses it, which is also what opens its popover. */
  focusOnCurrentItem = (): void => {
    const item = this.getCurrentItem();
    const target = item ? document.querySelector<HTMLElement>(`[data-field="${item.fieldPath}"]`) : null;
    // Already there — including a click on another cell of a highlighted row, which moves the cursor
    // to that row's error and must not take the focus away from the cell that was clicked.
    if (!target || target.contains(document.activeElement)) {
      return;
    }

    const focusable = target.matches(FOCUSABLE) ? target : target.querySelector<HTMLElement>(FOCUSABLE);
    focusable?.focus();
    this.bringIntoView(target);
  };

  /** Shows `items`, and brings the first into view if it is on screen already (without focusing it). */
  showErrors = (items: Array<ProductValidationItem>): void => {
    this.setItems(items);

    // Not `scrollToField`: that waits for a virtualized row to materialise and logs an error when it
    // never does, and only `line_items` is wired up to be scrolled to. A row the virtualizer hasn't
    // rendered is left where it is; the banner still says how many errors there are to find.
    const first = items[0];
    const target = first ? document.querySelector<HTMLElement>(`[data-field="${first.fieldPath}"]`) : null;
    if (target) {
      this.bringIntoView(target);
    }
  };

  clearErrors = (): void => {
    if (this.getItems().length > 0) {
      this.clearItems();
    }
  };

  /** The user changed this cell — see `withoutEditedField` for what that settles. */
  clearEditedField = (fieldPath: string): void => {
    const items = this.getItems();
    const remaining = withoutEditedField(items, fieldPath);
    if (remaining !== items) {
      this.setItems(remaining);
    }
  };

  /** The product on this row was marked for deletion — see `withoutRowErrors`. */
  clearRowErrors = (rowIndex: number): void => {
    const items = this.getItems();
    const remaining = withoutRowErrors(items, rowIndex);
    if (remaining !== items) {
      this.setItems(remaining);
    }
  };

  private highlightFor = (fieldPath: string): ProductValidationItem => {
    let highlight = this.highlights.get(fieldPath);
    if (!highlight) {
      highlight = {
        id: `validation-highlight:${fieldPath}`,
        fieldPath: rowPath(rowIndexOf(fieldPath)),
        severity: 'error',
        type: 'server',
        scope: 'row',
        messages: [],
      };
      this.highlights.set(fieldPath, highlight);
    }
    return highlight;
  };

  private bringIntoView = (target: HTMLElement): void => {
    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
}
